/**
 * ai-chat typed API (Phase 7). Every shape — path params, resolved value —
 * derives from the SERVER's route defs via the type-only import below (erased
 * at build; no server code reaches the bundle). A server-side shape change
 * breaks this file and its callers at compile time.
 *
 * Deliberately NOT here:
 * - POST /messages (and /stream, /send): SSE streaming — the page consumes the
 *   event stream with a hand-rolled fetch + reader (see chat.page.tsx).
 */
import { apiFetch, typedRoute } from "@d12labs/client";
import type { aiChatRouteDefs } from "../server/routes";

/** Rename's resolved value, derived from the server def's handler return type. */
type RenamedConversation = Exclude<
	Awaited<ReturnType<typeof aiChatRouteDefs.renameConversation.handler>>,
	Response
>;

export const aiChatApi = {
	profiles: typedRoute<typeof aiChatRouteDefs.profiles>()("GET", "/api/plugins/ai-chat/profiles"),
	conversations: typedRoute<typeof aiChatRouteDefs.conversations>()(
		"GET",
		"/api/plugins/ai-chat/conversations",
	),
	createConversation: typedRoute<typeof aiChatRouteDefs.createConversation>()(
		"POST",
		"/api/plugins/ai-chat/conversations",
	),
	conversation: typedRoute<typeof aiChatRouteDefs.conversation>()(
		"GET",
		"/api/plugins/ai-chat/conversations/:id",
	),
	// The PATCH body is hand-parsed server-side (the def declares no input schema,
	// keeping its exact 400 bodies) — typedRoute carries a body only when the def
	// declares one, so this stays a hand-rolled apiFetch with the same call shape
	// and a def-derived output type.
	renameConversation: (opts: {
		params: { id: string };
		input: { title: string };
	}): Promise<RenamedConversation> =>
		apiFetch<RenamedConversation>(
			`/api/plugins/ai-chat/conversations/${encodeURIComponent(opts.params.id)}`,
			{ method: "PATCH", body: JSON.stringify(opts.input) },
		),
	removeConversation: typedRoute<typeof aiChatRouteDefs.removeConversation>()(
		"DELETE",
		"/api/plugins/ai-chat/conversations/:id",
	),
};

/** The server's conversation row shape, derived — never redeclared by hand. */
export type Conversation = Awaited<ReturnType<typeof aiChatApi.conversations>>[number];

/** GET /conversations/:id payload ({ conversation, messages }), derived. */
export type ConversationDetail = Awaited<ReturnType<typeof aiChatApi.conversation>>;
